import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { GMB_SERVICE, IGmbService } from '@boxx/gmb-core';
import {
  IOnboardingService,
  ONBOARDING_SERVICE,
} from '../../services/IOnboarding.service';
import * as fromBusinessConfigActions from '../onboarding.actions';
import { OnboardingStore } from '@boxx/onboarding-core';

@Injectable()
export class OnboardingBusinessEffects {
  getBusinessData$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromBusinessConfigActions.getBusinessDataBeginAction),
      switchMap((action) =>
        this.service.getBusinessData().pipe(
          map((payload) =>
            fromBusinessConfigActions.getBusinessDataSuccessAction({
              payload,
            })
          ),
          catchError((errors) =>
            of([
              fromBusinessConfigActions.getBusinessDataFailAction({
                errors,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          )
        )
      )
    )
  );

  getLocationStatus = createEffect(() =>
    this.actions.pipe(
      ofType(fromBusinessConfigActions.getLocationStatusBeginAction),
      withLatestFrom(this.onboardingStore.businessConfig.businessData$),
      switchMap(([_, businessData]) => {
        if (
          !businessData?.config?.hasGmbAccount &&
          !businessData?.config?.gmbCreateOrSync
        ) {
          return of(
            fromBusinessConfigActions.getLocationStatusSuccessAction({
              payload: { status: 'NO_GMB', data: null },
            })
          );
        }

        return this.gmbService.getLocation().pipe(
          mergeMap((gmbData) => {
            if (
              !businessData?.config?.hasGmbAccount &&
              gmbData.status === 'NO_GMB'
            ) {
              return of([
                fromBusinessConfigActions.getLocationStatusSuccessAction({
                  payload: { status: 'IN_PROCESS', data: null },
                }),
                fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
              ]).pipe(switchMap((d) => d));
            }

            return of([
              fromBusinessConfigActions.getLocationStatusSuccessAction({
                payload: gmbData,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          }),
          catchError((errors) =>
            of([
              fromBusinessConfigActions.getLocationStatusFailAction({
                errors,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          )
        );
      })
    )
  );

  updateBusinessData$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromBusinessConfigActions.updateBusinessDataBeginAction),
      switchMap((action) =>
        this.service.updateBusinessData(action.payload).pipe(
          mergeMap((payload) =>
            of([
              fromBusinessConfigActions.updateBusinessDataSuccessAction({
                payload,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          ),
          catchError((errors) =>
            of([
              fromBusinessConfigActions.updateBusinessDataFailAction({
                errors,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          )
        )
      )
    )
  );

  updateBusinessInfo$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromBusinessConfigActions.updateBusinessInfoBeginAction),
      switchMap((action) =>
        this.service.updateBusinessInfoData(action.payload).pipe(
          mergeMap((payload) =>
            of([
              fromBusinessConfigActions.updateBusinessInfoSuccessAction({
                payload,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          ),
          catchError((errors) =>
            of([
              fromBusinessConfigActions.updateBusinessInfoFailAction({
                errors,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          )
        )
      )
    )
  );

  updateStoreData$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromBusinessConfigActions.updateStoreInfoBeginAction),
      switchMap((action) =>
        this.service.setTokenEcwid(action.payload).pipe(
          mergeMap((payload) =>
            of([
              fromBusinessConfigActions.updateStoreInfoSuccessAction({
                payload,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          ),
          catchError((errors) =>
            of([
              fromBusinessConfigActions.updateStoreInfoFailAction({
                errors,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          )
        )
      )
    )
  );

  updateMeta$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromBusinessConfigActions.updateMetaBeginAction),
      switchMap((action) =>
        from(this.service.updateMeta(action.payload)).pipe(
          map((payload) =>
            fromBusinessConfigActions.updateMetaSuccessAction({
              payload,
            })
          ),
          catchError((errors) =>
            of(
              fromBusinessConfigActions.updateMetaFailAction({
                errors,
              })
            )
          )
        )
      )
    )
  );

  createWebsiteData$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromBusinessConfigActions.createWebsiteBeginAction),
      switchMap(() =>
        this.service.createWebsite().pipe(
          map((payload) =>
            fromBusinessConfigActions.createWebsiteSuccessAction({
              payload,
            })
          ),
          catchError((errors) =>
            of([
              fromBusinessConfigActions.createWebsiteFailAction({
                errors,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          )
        )
      )
    )
  );

  deleteBusinessImage$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromBusinessConfigActions.deleteBusinessImageBeginAction),
      withLatestFrom(this.onboardingStore.businessConfig.businessData$),
      switchMap(([action, businessData]) =>
        this.service.deleteBusinessImage(action.payload).pipe(
          mergeMap((response) => {
            const payload = { ...businessData.images };
            switch (action.payload.container) {
              case 'logo':
                payload.logo_image = null;
                break;
              case 'about':
                payload.about_image = null;
                break;
              case 'gallery':
                payload.gallery = payload.gallery.filter(
                  (image) => image.index !== action.payload.index
                );
                break;
            }
            return of ([
              fromBusinessConfigActions.deleteBusinessImageSuccessAction({
                payload
              }),
              fromBusinessConfigActions.clearRemoveImageSite()
            ]).pipe(switchMap((d) => d));
             
          }),
          catchError((errors) =>
            /* of(
                fromBusinessConfigActions.deleteBusinessImageFailAction({
                  errors,
                })
              ) */
            of([
              fromBusinessConfigActions.deleteBusinessImageFailAction({
                errors,
              }),
              fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          )
        )
      )
    )
  );

  saveScoreData$ = createEffect(() =>
  this.actions.pipe(
    ofType(fromBusinessConfigActions.updateScoreInfoBeginAction),
    switchMap((action) =>
      this.service.saveScoreData(action.payload).pipe(

        map((response) => {
          const payload = action.payload;
          return fromBusinessConfigActions.updateScoreInfoSuccessAction({
            payload,
          });
        }),
        // mergeMap((payload) =>
        //     of([
        //       fromBusinessConfigActions.updateScoreInfoSuccessAction({
        //         payload,
        //       }),
        //       fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
        //     ]).pipe(switchMap((d) => d))
        //   ),
        catchError((errors) =>
          of([
            fromBusinessConfigActions.updateScoreInfoFailAction({
              errors,
            }),
            fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction(),
          ]).pipe(switchMap((d) => d))
        )
      )
    )
  )
);

  constructor(
    private actions: Actions,
    private onboardingStore: OnboardingStore,
    @Inject(ONBOARDING_SERVICE) private service: IOnboardingService,
    @Inject(GMB_SERVICE) private gmbService: IGmbService
  ) {}
}
